import { FileItem } from '@/types'

/**
 * Single source of truth for how files are ordered in the file table.
 *
 * `FileTable` renders this order and `useNavigation.selectFileRange` consumes the
 * ids it produces, so shift-click ranges always follow what is on screen.
 */

export type FileSortColumn = 'name' | 'file_size' | 'updated_at' | 'type'

export type SortDirection = 'asc' | 'desc'

export interface FileSort {
  column: FileSortColumn
  direction: SortDirection
}

/** Whitelist used to validate persisted preferences. */
export const FILE_SORT_COLUMNS: readonly FileSortColumn[] = [
  'name',
  'file_size',
  'updated_at',
  'type',
]

export const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc']

export const DEFAULT_FILE_SORT: FileSort = { column: 'name', direction: 'asc' }

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const

export const DEFAULT_PAGE_SIZE = 100

/**
 * The name the row actually renders. Sorting on `file.name` instead would put
 * renamed files in the wrong place, since the cell shows the display name.
 */
export function getFileDisplayName(file: FileItem): string {
  return file.display_name || file.original_name || file.name || ''
}

/**
 * Short, uppercase type label — `PDF`, `DOCX`, `PNG`. Falls back to the mime
 * subtype for files whose name carries no extension.
 */
export function getFileTypeLabel(file: FileItem): string {
  const name = getFileDisplayName(file)
  const dotIndex = name.lastIndexOf('.')

  // A leading dot is a hidden file, not an extension.
  if (dotIndex > 0 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toUpperCase()
  }

  const subtype = file.mime_type?.split('/')[1]
  return subtype ? subtype.toUpperCase() : ''
}

const getSortableSize = (file: FileItem): number =>
  typeof file.file_size === 'number' && Number.isFinite(file.file_size) ? file.file_size : 0

const getSortableTimestamp = (file: FileItem): number => {
  const parsed = file.updated_at ? Date.parse(file.updated_at) : NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Order `files` by `sort`. Pass `null` to keep the given order untouched — that
 * is the recently-viewed case, where recency is the point.
 *
 * The collator is passed in rather than built here: `localeCompare` constructs
 * one per call, which dominates the cost across thousands of comparisons.
 */
export function sortFiles(
  files: FileItem[],
  sort: FileSort | null,
  collator: Intl.Collator
): FileItem[] {
  if (!sort) return files

  const direction = sort.direction === 'desc' ? -1 : 1

  // Copy first — the input comes from a frozen Redux slice.
  return [...files].sort((a, b) => {
    let comparison = 0

    switch (sort.column) {
      case 'file_size':
        comparison = getSortableSize(a) - getSortableSize(b)
        break
      case 'updated_at':
        comparison = getSortableTimestamp(a) - getSortableTimestamp(b)
        break
      case 'type':
        comparison = collator.compare(getFileTypeLabel(a), getFileTypeLabel(b))
        break
      case 'name':
      default:
        comparison = collator.compare(getFileDisplayName(a), getFileDisplayName(b))
        break
    }

    // Stable tiebreak so equal keys don't reshuffle between renders.
    if (comparison === 0) return a.id - b.id

    return comparison * direction
  })
}
